/**
 * The world layer: everything Football 101 draws ON the field, in screen space from `ui.teach` (projected by the
 * lesson engine with the renderer's camera every frame):
 *
 *   - the BUBBLE over your head: the one button to press now (from `view.now`, device caps);
 *   - "YOU" over your head (early lessons);
 *   - the opponent's TELL glyph (⬇ LOW / ⬆ HIGH / CROUCH / JUMP);
 *   - turf guides: cones, flags, zones, spot markers, chevron arrows;
 *   - tags on the painted lines (blue line, yellow line, end zone) with edge pointers when off screen;
 *   - the landing ring of a pass (green past the line, red behind it) and the toss meter: a big horizontal bar by
 *     the head with its green band, a "LET GO!" call while the fill is in it, and — after the toss — a mark where
 *     the player let go (TOO SOON / PERFECT / TOO LATE);
 *   - a turf guide that ends up behind the coach panel lifts its label above the panel (the panel never fades);
 *   - "THIS WAY" when you run backwards, the slow-mo vignette.
 *
 * Positions change every frame and are written straight to the DOM in a rAF (no Preact render per frame); the
 * contents (caps, labels) render through Preact when the view changes.
 */
import { useEffect, useRef } from 'preact/hooks';
import { ui } from '@hf/client-core/ui-model/store';
import type { TutorialView } from '@hf/client-core/ui-model/types';
import type { TeachCue } from '@hf/client-core/ui-model/tutorial/catalog';
import { NowCaps, useTutorialDevice } from './keys';
import {
  dodgeFaces,
  faceBox,
  headHoles,
  type DodgeDir,
  type FaceHead,
} from '@hf/client-core/ui-model/tutorial/head-dodge';
import { coveredBy, type Box } from '@hf/client-core/ui-model/tutorial/panel-cover';
import type {
  ScreenArrow,
  ScreenLine,
  ScreenMarker,
  TeachFrame,
} from '@hf/client-core/ui-model/tutorial/teach-frame';

/** One vocabulary everywhere: the tell names the move he is about to make, in the counter square's words. */
const CUE: Record<TeachCue, { glyph: string; word: string; tone: string }> = {
  dive: { glyph: '⬇', word: 'DIVE', tone: 'red' },
  leap: { glyph: '⬆', word: 'LEAP', tone: 'blue' },
  truck: { glyph: '▼', word: 'TRUCK', tone: 'gold' },
  hurdle: { glyph: '⬆', word: 'HURDLE', tone: 'blue' },
};

export function WorldLayer({ view }: { view: TutorialView }) {
  const device = useTutorialDevice();
  const root = useRef<HTMLDivElement>(null);
  const bubble = useRef<HTMLDivElement>(null);
  const you = useRef<HTMLDivElement>(null);
  const cue = useRef<HTMLDivElement>(null);
  const meter = useRef<HTMLDivElement>(null);
  const guides = useRef<HTMLDivElement>(null);
  const lines = useRef<HTMLDivElement>(null);
  const thisWay = useRef<HTMLDivElement>(null);
  const vignette = useRef<HTMLDivElement>(null);
  const now = view.phase === 'beat' ? view.now : null;
  useTouchPrompt(now);
  const liveRef = useRef(false);
  liveRef.current = !!view.live;

  useEffect(() => {
    let raf = 0;
    let last = -1;
    const pool = new GuidePool();
    const loop = (): void => {
      raf = requestAnimationFrame(loop);
      const t = ui.teach;
      if (t.version === last) return;
      last = t.version;
      const on = t.active;
      if (root.current) root.current.style.visibility = on ? 'visible' : 'hidden';
      if (!on) return;
      place(you.current, t.head.x, t.head.y - 10 * scaleU(), t.head.on && t.you);
      if (cue.current) {
        place(cue.current, t.opp.x, t.opp.y - 12 * scaleU(), t.opp.on && t.cue !== null);
        const c = t.cue ? CUE[t.cue] : null;
        if (c && cue.current.dataset['cue'] !== t.cue) {
          cue.current.dataset['cue'] = t.cue ?? '';
          cue.current.className = `hf-tell ${c.tone}`;
          const g = cue.current.querySelector('.hf-tell__glyph');
          const w = cue.current.querySelector('.hf-tell__word');
          if (g) g.textContent = c.glyph;
          if (w) w.textContent = c.word;
        }
      }
      // after the tell: the bubble steps around it (and the coach panel, the thumbs, the top HUD, his head)
      if (t.head.on) placeTouchCoach(t, liveRef.current);
      placeBubble(bubble.current, t, cue.current);
      if (meter.current) {
        const m = t.meter;
        const el = meter.current;
        // beside the head (the HOLD bubble sits above it): never under the bubble, never over the posts
        place(el, t.head.x + 120 * scaleU(), t.head.y + 34 * scaleU(), !!m && t.head.on);
        if (m) {
          const done = m.release >= 0;
          const band = m.lo >= 0;
          const fill = done ? m.release : m.fill;
          el.style.setProperty('--fill', fill.toFixed(4));
          el.style.setProperty('--lo', String(Math.max(0, m.lo)));
          el.style.setProperty('--hi', String(Math.max(0, m.hi)));
          el.classList.toggle('band', band);
          // the band is in charge frames; half a frame of slack on each side matches the release tick
          const half = 0.5 / 50;
          const inBand = band && fill >= m.lo - half && fill <= m.hi + half;
          el.classList.toggle('in', inBand);
          el.classList.toggle('done', done);
          const verdict = !done || !band ? '' : inBand ? 'PERFECT!' : fill < m.lo ? 'TOO SOON' : 'TOO LATE';
          // past the green while still holding: it is already too strong — say so (it used to go back to "HOLD…")
          const over = !done && band && fill > m.hi + half;
          el.classList.toggle('over', over);
          el.dataset['verdict'] = done ? (inBand ? 'good' : 'miss') : '';
          const call = el.querySelector('.hf-meter__call');
          const text = done
            ? verdict
            : inBand
              ? 'LET GO!'
              : over
                ? m.kind === 'throw'
                  ? 'TOO FAR!'
                  : 'TOO MUCH!'
                : 'HOLD…';
          if (call && call.textContent !== text) call.textContent = text;
          const title = el.querySelector('.hf-meter__title');
          if (title && title.textContent !== m.title) title.textContent = m.title;
        }
      }
      if (thisWay.current)
        place(thisWay.current, t.head.x + 90 * scaleU(), t.head.y + 30 * scaleU(), t.thisWay);
      if (vignette.current) vignette.current.style.opacity = String(t.vignette * 0.85);
      if (guides.current) {
        const b = bubble.current;
        const bub = b && b.classList.contains('on') && t.head.on ? b.firstElementChild : null;
        const mt = meter.current && meter.current.classList.contains('show') ? meter.current : null;
        pool.draw(
          guides.current,
          t,
          coachRects(),
          bub ? bub.getBoundingClientRect() : null,
          lines.current,
          mt ? mt.getBoundingClientRect() : null,
        );
      }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div class="hf-world" ref={root} aria-hidden="true">
      <div class="hf-world__vignette" ref={vignette} />
      {/* the painted-line tags sit BELOW the coach panel (a pulsing yellow line was drawn across its text) */}
      <div class="hf-world__lines" ref={lines} />
      <div class="hf-world__guides" ref={guides} />
      <div class="hf-you t-broadcast" ref={you}>
        YOU
      </div>
      <div class="hf-tell" ref={cue}>
        <span class="hf-tell__glyph" />
        <span class="hf-tell__word t-broadcast" />
      </div>
      <div class="hf-meter" ref={meter}>
        <span class="hf-meter__title t-broadcast" />
        <span class="hf-meter__bar">
          <span class="hf-meter__band" />
          <span class="hf-meter__fill" />
          <span class="hf-meter__mark" />
        </span>
        <span class="hf-meter__call t-display" />
      </div>
      <div class="hf-thisway t-display" ref={thisWay}>
        THIS WAY ▶
      </div>
      <div
        class={`hf-bubble ${now ? 'on' : ''} ${view.idle ? 'idle' : ''} mode-${now?.mode ?? 'none'} ${now?.tone ? `tone-${now.tone}` : ''}`}
        ref={bubble}
      >
        {now ? (
          <div class="hf-bubble__in" key={`${now.button}${now.mode}${now.verb}${now.tone ?? ''}`}>
            <NowCaps now={now} device={device} />
            <span class="hf-bubble__verb t-broadcast">{now.verb}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** On-screen touch buttons (input/touch.ts `data-btn`) of each logical button. */
const TOUCH_KEY: Record<string, string> = {
  L: 'left',
  R: 'right',
  D: 'down',
  U: 'jump',
  A: 'action',
  S: 'special',
};

/**
 * Touch: the on-screen button the bubble asks for pulses (tutorial.css `:root[data-tut-btn]`): a first-time phone
 * player is shown WHICH thumb button, not only a glyph (QA round 2).
 */
const useTouchPrompt = (now: TutorialView['now'] | null | undefined): void => {
  // (GET READY is no press yet: the thumb button pulses once it is time)
  const a = now && now.mode !== 'wait' && now.tone !== 'ready' ? TOUCH_KEY[now.button] : undefined;
  const b =
    now && now.mode !== 'wait' && now.then && now.then !== now.button ? TOUCH_KEY[now.then] : undefined;
  useEffect(() => {
    const ds = document.documentElement.dataset;
    if (a) ds['tutBtn'] = a;
    else delete ds['tutBtn'];
    if (b) ds['tutBtn2'] = b;
    else delete ds['tutBtn2'];
  }, [a, b]);
  useEffect(
    () => () => {
      const ds = document.documentElement.dataset;
      delete ds['tutBtn'];
      delete ds['tutBtn2'];
    },
    [],
  );
};

/** A screen rectangle (CSS px). */
interface Rect {
  l: number;
  r: number;
  t: number;
  b: number;
}

const overlap = (a: Rect, b: Rect): number =>
  Math.max(0, Math.min(a.r, b.r) - Math.max(a.l, b.l)) * Math.max(0, Math.min(a.b, b.b) - Math.max(a.t, b.t));

const rectOf = (el: Element | null | undefined): Rect | null => {
  const r = el?.getBoundingClientRect();
  return r && r.width > 2 && r.height > 2 ? { l: r.left, r: r.right, t: r.top, b: r.bottom } : null;
};

/**
 * What the bubble over your head must never cover: the coach panel (QA round 2: on a phone the top-docked panel hid
 * the prompt, only its HOLD tag stuck out), the on-screen thumb buttons, the top HUD's rush ring (a leap carried the
 * bubble into it), the opponent's TELL (the HOLD S bubble sat on the LEAP ⬆ it teaches you to read) and his head.
 */
const bubbleObstacles = (t: TeachFrame, tell: HTMLElement | null): Rect[] => {
  const out: Rect[] = [];
  for (const b of coachRects()) out.push(b);
  const rush = rectOf(document.querySelector('.hf-hud .hf-rush'));
  if (rush) out.push(rush);
  for (const b of document.querySelectorAll(
    '.hf-touch:not([hidden]) .hf-t-btn, .hf-touch:not([hidden]) .hf-t-pause',
  )) {
    const r = rectOf(b);
    if (r) out.push(r);
  }
  if (tell && tell.classList.contains('show')) {
    for (const part of tell.children) {
      const r = rectOf(part);
      if (r) out.push(r);
    }
  }
  if (t.opp.on && t.opp.r > 0) {
    const r = t.opp.r;
    out.push({ l: t.opp.x - r, r: t.opp.x + r, t: t.opp.y, b: t.opp.y + 2 * r });
  }
  return out;
};

type Spot = 'above' | 'right' | 'left' | 'below';

/**
 * The bubble over your head. It points at YOUR head (its tail), above it; when that spot is taken (coach panel, thumb
 * buttons, top HUD, his tell, his head) it slides sideways — the tail still pointing at your head — or moves beside
 * your head (tail sideways), or under it. Placements are judged from the bubble's layout box (its pop / bounce
 * animations never move the choice), and a placement holds a moment before it changes (no flicker).
 */
const placeBubble = (el: HTMLElement | null, t: TeachFrame, tell: HTMLElement | null): void => {
  if (!el) return;
  const u = scaleU();
  const x = t.head.x;
  const above = t.head.y - 14 * u;
  const inner = el.firstElementChild as HTMLElement | null;
  const show = t.head.on;
  if (!inner || !show || !el.classList.contains('on')) {
    place(el, x, above, show);
    setSpot(el, 'above');
    el.style.setProperty('--tail', '0px');
    return;
  }
  const w = inner.offsetWidth;
  const h = inner.offsetHeight;
  const tail = 26 * u;
  const r = Math.max(t.head.r, 20 * u);
  const mid = t.head.y + r;
  // under the head (its radius comes from the projection), tail up
  const below = t.head.y + 2 * r + 26 * u;
  const side = r + 30 * u;
  const obstacles = bubbleObstacles(t, tell);
  const W = t.w;
  const H = t.h;
  const box = (spot: Spot, dx: number): Rect => {
    switch (spot) {
      case 'below':
        return { l: x + dx - w / 2, r: x + dx + w / 2, t: below - tail, b: below + h };
      case 'right':
        return { l: x + side - tail, r: x + side + w, t: mid - h / 2, b: mid + h / 2 };
      case 'left':
        return { l: x - side - w, r: x - side + tail, t: mid - h / 2, b: mid + h / 2 };
      default:
        return { l: x + dx - w / 2, r: x + dx + w / 2, t: above - h, b: above + tail };
    }
  };
  const cost = (spot: Spot, dx: number): number => {
    const b = box(spot, dx);
    let c = 0;
    for (const o of obstacles) c += overlap(b, o);
    // off screen counts like an obstacle
    c += Math.max(0, 8 - b.l) * h + Math.max(0, b.r - (W - 8)) * h;
    c += Math.max(0, 8 - b.t) * w + Math.max(0, b.b - (H - 8)) * w;
    return c;
  };
  const reach = w / 2 - 24 * u;
  // preference (lower = better): above › a slide the tail follows › beside › a long slide › below
  const rank: Record<Spot, number> = { above: 0, right: 2 * w, left: 2 * w, below: 4 * w };
  let best = { spot: 'above' as Spot, dx: 0, c: Infinity, d: Infinity };
  const consider = (spot: Spot, dx: number): void => {
    const c = cost(spot, dx);
    const d =
      rank[spot] + (Math.abs(dx) > reach ? 3 * w : 0) + Math.abs(dx) + (spot === lastSpot(el) ? -w / 2 : 0);
    if (c < best.c - 1 || (Math.abs(c - best.c) <= 1 && d < best.d)) best = { spot, dx, c, d };
  };
  for (const spot of ['above', 'below'] as const) {
    const b0 = box(spot, 0);
    const dxs = [0];
    for (const o of obstacles) {
      if (overlap(b0, o) <= 0) continue;
      dxs.push(o.l - 10 * u - b0.r, o.r + 10 * u - b0.l);
    }
    if (b0.l < 8) dxs.push(8 - b0.l);
    if (b0.r > W - 8) dxs.push(W - 8 - b0.r);
    for (const dx of dxs) if (Math.abs(dx) <= w) consider(spot, dx);
  }
  consider('right', 0);
  consider('left', 0);
  const spot = best.spot;
  if (spot === 'right') place(el, x + side, mid, true);
  else if (spot === 'left') place(el, x - side, mid, true);
  else place(el, x + best.dx, spot === 'below' ? below : above, true);
  setSpot(el, spot);
  el.style.setProperty(
    '--tail',
    `${spot === 'above' || spot === 'below' ? Math.max(-reach, Math.min(reach, -best.dx)).toFixed(1) : 0}px`,
  );
};

const lastSpot = (el: HTMLElement): Spot => (el.dataset['spot'] as Spot | undefined) ?? 'above';
const setSpot = (el: HTMLElement, spot: Spot): void => {
  if (el.dataset['spot'] !== spot) el.dataset['spot'] = spot;
};

/**
 * Touch: the coach panel docks at the top on the side AWAY from your head (centred it spanned the whole top of a
 * phone, so the bubble had nowhere to go). It only changes sides while the ball is dead.
 */
const placeTouchCoach = (t: TeachFrame, live: boolean): void => {
  const coach = document.querySelector<HTMLElement>('.hf-hud.touch-on .hf-tutorial .hf-coach');
  if (!coach) return;
  const cur = coach.dataset['side'];
  const x = t.head.x / Math.max(1, t.w);
  let want = cur ?? (x < 0.5 ? 'right' : 'left');
  if (!live || !cur) {
    if (x < 0.42) want = 'right';
    else if (x > 0.58) want = 'left';
  }
  if (cur !== want) coach.dataset['side'] = want;
};

/**
 * The coach panel's opaque parts: its body, the lesson tab and the key strip (the panel's box also spans the empty
 * space between the tab and the strip: a cone standing there in plain view had its mark hidden — QA round 1).
 */
const coachRects = (): Box[] => {
  const coach = document.querySelector('.hf-tutorial .hf-coach');
  if (!coach) return [];
  const out: Box[] = [];
  for (const sel of ['.hf-coach__body', '.hf-coach__tab', '.hf-coach__aux']) {
    const r = coach.querySelector(sel)?.getBoundingClientRect();
    if (r && r.width > 0 && r.height > 0)
      out.push({ l: r.left, r: r.right, t: r.top, b: r.bottom, text: sel === '.hf-coach__body' });
  }
  return out;
};

/** One design px (1920×1080 mocks) in CSS px. */
const scaleU = (): number =>
  typeof window === 'undefined' ? 1 : Math.min(window.innerWidth / 1920, window.innerHeight / 1080);

/** A layer's head-hole mask (head-dodge.ts `headHoles`; '' = none), written only when it changes. */
const setMask = (el: HTMLElement, mask: string): void => {
  if ((el.dataset['mask'] ?? '') === mask) return;
  el.dataset['mask'] = mask;
  el.style.maskImage = mask;
  el.style.setProperty('-webkit-mask-image', mask);
  // two holes: keep only where BOTH layers are opaque
  el.style.setProperty('mask-composite', mask.includes('), radial') ? 'intersect' : '');
  el.style.setProperty('-webkit-mask-composite', mask.includes('), radial') ? 'source-in' : '');
};

const place = (el: HTMLElement | null, x: number, y: number, show: boolean): void => {
  if (!el) return;
  el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
  el.classList.toggle('show', show);
};

/** Pooled DOM for the turf guides (re-used every frame; created on demand). */
class GuidePool {
  private markers: HTMLElement[] = [];
  private arrows: HTMLElement[] = [];
  private lines: HTMLElement[] = [];
  private ring: HTMLElement | null = null;
  private panel: readonly Box[] = [];
  private bubble: DOMRect | null = null;
  private w = 1280;
  /** The throw / toss meter's box (labels under it step back, like under the bubble). */
  private meterBox: DOMRect | null = null;
  /** Your head on screen (a marker under it steps back). */
  private head: TeachFrame['head'] | null = null;
  /** Both heads' boxes (YOU / his tell included): no tag sits on them (head-dodge.ts). */
  private faces: Box[] = [];
  private screenH = 720;
  /** The translate currently applied to each label (to measure where it sits without it). */
  private readonly shift = new WeakMap<HTMLElement, { x: number; y: number }>();
  /** The side each label stepped to off a face (kept while needed: no flicker). */
  private readonly dodgeDir = new WeakMap<HTMLElement, DodgeDir>();

  draw(
    host: HTMLElement,
    t: TeachFrame,
    panel: readonly Box[],
    bubble: DOMRect | null = null,
    lineHost: HTMLElement | null = null,
    meter: DOMRect | null = null,
  ): void {
    this.panel = panel;
    this.bubble = bubble && bubble.width > 0 ? bubble : null;
    this.meterBox = meter && meter.width > 0 ? meter : null;
    this.w = t.w;
    this.screenH = t.h;
    this.head = t.head.on && t.head.r > 0 ? t.head : null;
    const u0 = scaleU();
    this.faces = [];
    // YOU rides over your head, his tell over his: a tag on them is as bad as one on the face
    if (this.head) this.faces.push(faceBox(t.head, t.you ? 56 * u0 : 0));
    if (t.opp.on && t.opp.r > 0) this.faces.push(faceBox(t.opp, t.cue ? 70 * u0 : 0));
    this.sync(host, this.markers, t.markers.length, 'hf-mark');
    t.markers.forEach((m, i) => this.marker(this.markers[i]!, m, t.w));
    this.sync(host, this.arrows, t.arrows.length, 'hf-arrow');
    t.arrows.forEach((a, i) => this.arrow(this.arrows[i]!, a));
    this.sync(lineHost ?? host, this.lines, t.lines.length, 'hf-line');
    t.lines.forEach((l, i) => this.line(this.lines[i]!, l, t.w));
    if (!this.ring) {
      this.ring = document.createElement('div');
      this.ring.className = 'hf-ring';
      this.ring.innerHTML = '<i class="hf-ring__disc"></i><span class="hf-ring__tag t-broadcast"></span>';
      host.appendChild(this.ring);
    }
    const r = t.landing;
    // off screen it becomes an edge pointer ("IT LANDS HERE ▶"): the first throw's ring left the screen 0.24 s into
    // the hold and the player had no landing cue for the rest of it (QA round 4)
    const roff = r && !r.on ? (r.x < 0 ? -1 : 1) : 0;
    this.ring.classList.toggle('show', !!r);
    this.ring.classList.toggle('off', roff !== 0);
    this.ring.classList.toggle('left', roff < 0);
    this.ring.classList.toggle('right', roff > 0);
    if (r && roff !== 0) {
      const tag = this.ring.lastElementChild as HTMLElement;
      const text = `${roff < 0 ? '◀ ' : ''}${r.label || 'HERE'}${roff > 0 ? ' ▶' : ''}`;
      if (tag.textContent !== text) tag.textContent = text;
      this.ring.style.transform = `translate3d(${roff < 0 ? 12 : t.w - 12}px, ${r.y.toFixed(1)}px, 0)`;
      this.ring.classList.toggle('ok', r.ok);
      this.ring.classList.toggle('go', r.cue === 'go');
      this.ring.classList.toggle('far', r.cue === 'far');
      this.ring.classList.toggle('short', r.cue === 'short');
      this.ring.classList.remove('under');
      this.put(tag, 0, 0, 'up');
    } else if (r) {
      this.ring.style.transform = `translate3d(${r.x.toFixed(1)}px, ${r.y.toFixed(1)}px, 0)`;
      if (r.r > 0) this.ring.style.setProperty('--ring-r', `${r.r.toFixed(1)}px`);
      else this.ring.style.removeProperty('--ring-r');
      this.ring.classList.toggle('ok', r.ok);
      // the release cue: the ring itself turns green (LET GO!) inside the target zone, red past it
      this.ring.classList.toggle('go', r.cue === 'go');
      this.ring.classList.toggle('far', r.cue === 'far');
      this.ring.classList.toggle('short', r.cue === 'short');
      const tag = this.ring.lastElementChild as HTMLElement;
      const u = scaleU();
      const top = coveredBy(
        { l: r.x - 70 * u, r: r.x + 70 * u, t: r.y - 20 * u, b: r.y + 20 * u },
        this.panel,
      );
      this.ring.classList.toggle('under', top !== null);
      const text = top !== null && r.label ? `${r.label} ▼` : r.label;
      if (tag.textContent !== text) tag.textContent = text;
      const lift = this.liftFor(tag, top);
      // the tag stays on screen (a ring near the edge clipped "CATCH HE…")
      this.put(tag, this.clampX(r.x, tag), lift > 0 ? -lift : 0, 'up');
    }
    this.duckUnderBubble();
    this.holes(host, lineHost, t);
  }

  /**
   * Set a label's shift (`x`, `y`), stepped off any face it would cover (head-dodge.ts): `prefer` = the side to try
   * first, `slope` = dx per dy along its line.
   */
  private put(tag: HTMLElement, x: number, y: number, prefer: DodgeDir, slope = 0): void {
    let fx = x;
    let fy = y;
    const prev = this.shift.get(tag) ?? { x: 0, y: 0 };
    let dir: DodgeDir | null = null;
    if (this.faces.length > 0 && tag.textContent && tag.style.display !== 'none') {
      const cur = tag.getBoundingClientRect();
      if (cur.width > 0) {
        const box = {
          l: cur.left - prev.x + x,
          r: cur.right - prev.x + x,
          t: cur.top - prev.y + y,
          b: cur.bottom - prev.y + y,
        };
        const d = dodgeFaces(
          box,
          this.faces,
          this.panel,
          this.screenH,
          prefer,
          slope,
          this.dodgeDir.get(tag) ?? null,
          10 * scaleU(),
        );
        fx += d.dx;
        fy += d.dy;
        dir = d.dir;
      }
    }
    if (dir) this.dodgeDir.set(tag, dir);
    else this.dodgeDir.delete(tag);
    if (fx === prev.x && fy === prev.y && tag.style.translate !== '') return;
    this.shift.set(tag, { x: fx, y: fy });
    tag.style.translate = fx !== 0 || fy !== 0 ? `${fx.toFixed(1)}px ${fy.toFixed(1)}px` : '';
  }

  /**
   * The turf marks are drawn BEHIND the heads: each layer gets a mask with a hole per head its marks reach (QA round
   * 4: the reached cone across the Rookie's chin). Labels stepped off the faces already (`put`).
   */
  private holes(host: HTMLElement, lineHost: HTMLElement | null, t: TeachFrame): void {
    const heads: FaceHead[] = [t.head, t.opp];
    const marksOf = (els: HTMLElement[]): Box[] => {
      const out: Box[] = [];
      for (const el of els) {
        if (el.style.display === 'none') continue;
        const r = el.firstElementChild?.getBoundingClientRect();
        if (r && r.width > 0 && r.height > 0) out.push({ l: r.left, r: r.right, t: r.top, b: r.bottom });
      }
      return out;
    };
    const ring = this.ring && this.ring.classList.contains('show') ? [this.ring] : [];
    const at = (el: HTMLElement): { x: number; y: number } => {
      const r = el.getBoundingClientRect();
      return { x: r.left, y: r.top };
    };
    setMask(host, headHoles(heads, marksOf([...this.markers, ...this.arrows, ...ring]), at(host)));
    if (lineHost && lineHost !== host) setMask(lineHost, headHoles(heads, marksOf(this.lines), at(lineHost)));
  }

  /** Horizontal shift that keeps a label centred on `x` fully inside the screen (8 px margin). */
  private clampX(x: number, label: HTMLElement): number {
    const half = label.offsetWidth / 2;
    if (half <= 0) return 0;
    const lo = 8 + half;
    const hi = this.w - 8 - half;
    return x < lo ? lo - x : x > hi ? hi - x : 0;
  }

  /**
   * Labels under the bubble over your head step back (the J LEAP bubble used to sit on the GO! tag), and under the
   * throw meter (the BLUE LINE tag drew across the bar).
   */
  private duckUnderBubble(): void {
    const boxes = [this.bubble, this.meterBox].filter((b): b is DOMRect => b !== null);
    for (const el of [...this.lines, ...this.markers, ...(this.ring ? [this.ring] : [])]) {
      const tag = el.lastElementChild as HTMLElement | null;
      if (!tag || el.style.display === 'none') continue;
      let hit = false;
      if (boxes.length > 0 && tag.textContent) {
        const r = tag.getBoundingClientRect();
        hit = boxes.some((b) => r.right > b.left && r.left < b.right && r.bottom > b.top && r.top < b.bottom);
      }
      tag.classList.toggle('ducked', hit);
    }
  }

  /**
   * How far a guide's label must rise so its bottom clears `top` (the panel edge its guide hides behind; null = the
   * guide is in view: no lift). Measured from where the label sits unlifted, so it rises just enough.
   */
  private liftFor(label: HTMLElement, top: number | null): number {
    if (top === null || !label.textContent) return 0;
    const now = label.getBoundingClientRect();
    if (now.height <= 0) return 0;
    const bottom = now.bottom - (this.shift.get(label)?.y ?? 0);
    return Math.max(0, bottom - (top - 10 * scaleU()));
  }

  /** The drawn box of a marker (its turf mark, not its label), for `coveredBy`. */
  private markBox(m: ScreenMarker): Box {
    const u = scaleU();
    const x0 = Math.min(m.x, m.x2);
    const x1 = Math.max(m.x, m.x2);
    switch (m.kind) {
      case 'cone':
        return { l: m.x - 26 * u, r: m.x + 26 * u, t: m.y - 70 * u, b: m.y + 4 * u };
      case 'flag':
        return { l: m.x, r: m.x + 60 * u, t: m.y - 90 * u, b: m.y };
      case 'zone':
        return { l: x0, r: x1, t: m.y - 34 * u, b: m.y + 18 * u };
      default:
        return { l: m.x - 24 * u, r: m.x + 24 * u, t: m.y - 10 * u, b: m.y + 10 * u };
    }
  }

  private sync(host: HTMLElement, list: HTMLElement[], n: number, cls: string): void {
    while (list.length < n) {
      const el = document.createElement('div');
      el.className = cls;
      el.innerHTML = '<i></i><span class="t-broadcast"></span>';
      host.appendChild(el);
      list.push(el);
    }
    for (let i = 0; i < list.length; i++) list[i]!.style.display = i < n ? '' : 'none';
  }

  private label(el: HTMLElement, text: string): void {
    const s = el.lastElementChild as HTMLElement;
    if (s.textContent !== text) s.textContent = text;
    s.style.display = text ? '' : 'none';
  }

  private marker(el: HTMLElement, m: ScreenMarker, w: number): void {
    // a cone / flag off screen becomes an edge pointer ("◀ BACK HERE"); so does a zone wholly off screen (the Hail
    // Mary's THROW IT HERE, 12–18 yd ahead: its pointer turns green with LET GO! with the release cue)
    const lo = Math.min(m.x, m.x2);
    const hi = Math.max(m.x, m.x2);
    const off = m.kind !== 'zone' ? (m.x < 8 ? -1 : m.x > w - 8 ? 1 : 0) : hi < 8 ? -1 : lo > w - 8 ? 1 : 0;
    const cls = `hf-mark ${m.kind} ${m.tone} ${m.done ? 'done' : ''} ${off ? `off ${off < 0 ? 'left' : 'right'}` : ''}`;
    if (el.className !== cls) el.className = cls;
    const zw = Math.max(0, Math.abs(m.x2 - m.x));
    const x = off < 0 ? 12 : off > 0 ? w - 12 : Math.min(m.x, m.x2);
    el.style.transform = `translate3d(${x.toFixed(1)}px, ${m.y.toFixed(1)}px, 0)`;
    el.style.width = m.kind === 'zone' && !off ? `${zw.toFixed(1)}px` : '';
    const top = off ? null : coveredBy(this.markBox(m), this.panel);
    el.classList.toggle('under', top !== null);
    this.label(
      el,
      off
        ? `${off < 0 ? '◀ ' : ''}${m.edge || m.label || 'HERE'}${off > 0 ? ' ▶' : ''}`
        : top !== null && m.label
          ? `${m.label} ▼`
          : m.label,
    );
    const tag = el.lastElementChild as HTMLElement;
    const lift = this.liftFor(tag, top);
    // a guide near the screen edge keeps its whole label on screen ("◀ START" and "BACK HERE" were clipped)
    const sx = off ? 0 : this.clampX(m.kind === 'zone' ? (m.x + m.x2) / 2 : m.x, tag);
    // never across a face (QA round 3: a HOP HERE box over the Rookie's eyes; round 4: END ZONE on Big Tom's
    // helmet): a tag a head reaches steps above it (or down onto the turf), and a cone you already reached (done)
    // steps back under your head (the mark itself is drawn behind the heads: `holes`)
    this.put(tag, sx, -lift, m.kind === 'zone' || m.kind === 'spot' ? 'down' : 'up');
    const h = this.head;
    let onHead = false;
    if (h && !off && m.done && (m.kind === 'cone' || m.kind === 'flag')) {
      const face = faceBox(h);
      const b = this.markBox(m);
      onHead = b.r > face.l && b.l < face.r && b.b > face.t && b.t < face.b;
    }
    el.classList.toggle('onhead', onHead);
  }

  private arrow(el: HTMLElement, a: ScreenArrow): void {
    const cls = `hf-arrow ${a.tone} ${a.x2 < a.x1 ? 'back' : ''}`;
    if (el.className !== cls) el.className = cls;
    const x = Math.min(a.x1, a.x2);
    el.style.transform = `translate3d(${x.toFixed(1)}px, ${a.y.toFixed(1)}px, 0)`;
    el.style.width = `${Math.abs(a.x2 - a.x1).toFixed(1)}px`;
    const u = scaleU();
    const top = coveredBy(
      { l: Math.min(a.x1, a.x2), r: Math.max(a.x1, a.x2), t: a.y - 16 * u, b: a.y + 16 * u },
      this.panel,
    );
    el.classList.toggle('under', top !== null);
    this.label(el, a.label);
    const tag = el.lastElementChild as HTMLElement;
    const lift = this.liftFor(tag, top);
    // the label sits on the part of the arrow that is on screen (lesson 3's "+5 YARDS" ran off the left edge)
    const lo = Math.max(8, Math.min(a.x1, a.x2));
    const hi = Math.min(this.w - 8, Math.max(a.x1, a.x2));
    const mid = (a.x1 + a.x2) / 2;
    const seen = hi > lo ? (lo + hi) / 2 : mid;
    const sx = seen - mid + this.clampX(seen, tag);
    this.put(tag, sx, -lift, 'down');
  }

  private line(el: HTMLElement, l: ScreenLine, w: number): void {
    const off = l.off !== 0;
    const cls = `hf-line ${l.tone} ${l.pulse ? 'pulse' : ''} ${off ? `off ${l.off < 0 ? 'left' : 'right'}` : ''}`;
    if (el.className !== cls) el.className = cls;
    if (off) {
      // edge pointer: pinned to the side the line is on, at mid height
      const x = l.off < 0 ? 14 : w - 14;
      el.style.transform = `translate3d(${x}px, ${(l.yNear - 120).toFixed(1)}px, 0)`;
      el.style.height = '';
      el.style.setProperty('--lean', '0deg');
    } else {
      const dx = l.xNear - l.xFar;
      const dy = l.yNear - l.yFar;
      const len = Math.hypot(dx, dy);
      const lean = Math.atan2(dx, dy);
      el.style.transform = `translate3d(${l.xFar.toFixed(1)}px, ${l.yFar.toFixed(1)}px, 0)`;
      el.style.height = `${len.toFixed(1)}px`;
      el.style.setProperty('--lean', `${(-lean).toFixed(4)}rad`);
    }
    this.label(el, off ? `${l.off < 0 ? '◀ ' : ''}${l.label}${l.off > 0 ? ' ▶' : ''}` : l.label);
    // a line near the screen edge keeps its whole tag on screen ("10 YARDS: FIRST D…" ran off the right edge)
    const tag = el.lastElementChild as HTMLElement;
    if (off) {
      // the edge pointer ("MUST REACH THIS ▶") steps up off the coach panel's strip it landed on
      let dy = 0;
      const prev = this.shift.get(tag)?.y ?? 0;
      const r = tag.getBoundingClientRect();
      if (r.width > 0 && tag.textContent) {
        const box = { l: r.left, r: r.right, t: r.top - prev, b: r.bottom - prev };
        dy = dodgeFaces(box, this.panel, [], this.screenH, 'up', 0, 'up', 10 * scaleU()).dy;
      }
      this.shift.set(tag, { x: 0, y: dy });
      this.dodgeDir.delete(tag);
      tag.style.translate = dy !== 0 ? `0px ${dy.toFixed(1)}px` : '';
      return;
    }
    // a tag a head stands on slides DOWN its line onto the turf under the heads (QA round 4: BLUE LINE / WAIT FOR GO
    // over the Rookie's hair), or up above them when the panel takes the turf
    const slope = l.yNear !== l.yFar ? (l.xNear - l.xFar) / (l.yNear - l.yFar) : 0;
    this.put(tag, this.clampX(l.xFar, tag), 0, 'down', slope);
  }
}
